import React from 'react';
import {
  Heading,
  Text,
  Button,
  Box,
  ButtonText,
  HStack,
  Textarea,
  TextareaInput,
  Input,
  InputField,
  VStack,
  Icon,
  PaperclipIcon,
  LinkIcon,
  Progress,
  ProgressFilledTrack,
  Pressable,
} from '@gluestack-ui/themed';

import {
  Pencil, Trash, ExternalLink, CircleX, ChevronUp, ChevronDown,
} from 'lucide-react-native';
import type { Resource } from '@/common/api/models/resource';
import ResourceDigestStatus from './ResourceDigestStatus';

interface ExtendedResource extends Resource {
  /** Client-only flag set while a digest reprocess job is in flight. */
  reprocessing?: boolean;
  uploadProgress?: {
    progress: number;
    total: number;
    loaded: number;
    rate: number;
    uploadStartTime: number;
    step: 'preparing' | 'uploading' | 'completing' | 'analyzing_link' | 'error';
    error?: string;
  };
}

interface ResourceCardProps {
  resource: ExtendedResource;
  onChange: (resource: Resource) => void;
  onDelete: () => void;
  onOpen: () => void;
  onCancelUpload: () => void;
  onRetryDigest: () => void;
  isRetryingDigest: boolean;
  /** Epoch ms until which a digest job is known to be in flight for this resource. */
  digestPendingUntil?: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  editMode?: boolean;
  readonly?: boolean;
  showExamRelevant?: boolean;
  onOralExamRelevantChange?: (value: boolean) => void;
  isOralExamRelevantUpdating?: boolean;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  onChange,
  onDelete,
  onOpen,
  onCancelUpload,
  onRetryDigest,
  isRetryingDigest,
  digestPendingUntil,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  editMode = false,
  readonly = false,
  showExamRelevant = false,
  onOralExamRelevantChange,
  isOralExamRelevantUpdating = false,
}) => {
  const [inEditMode, setInEditMode] = React.useState(editMode);

  // Local state only for unsaved changes during editing
  const [draft, setDraft] = React.useState({
    title: resource?.title || '',
    description: resource?.description || '',
    citation: resource?.citation || '',
    url: resource?.url || '',
  });

  const toggleEditMode = () => {
    if (!inEditMode) {
      // Enter edit mode: snapshot the current props
      setDraft({
        title: resource?.title || '',
        description: (resource?.description as string) || '',
        citation: resource?.citation || '',
        url: resource?.url || '',
      });
    }
    setInEditMode(!inEditMode);
  };

  const saveChanges = () => {
    // position is the row index. Sending it again asks the server to reorder
    // the card into the slot it already occupies, which rewrites the sort and
    // the card jumps back.
    const next = {
      ...resource,
      title: draft.title,
      description: draft.description || null,
      citation: draft.citation || '',
      url: draft.url || '',
    };
    delete next.position;
    onChange(next);
    setInEditMode(false);
  };

  const humanReadableRemainingTime = (secondsRemaining: number) => {
    if (secondsRemaining < 60) {
      return `${secondsRemaining} sec`;
    }
    return `${Math.round(secondsRemaining / 60)} min`;
  };

  // calculate human readable filesize in KB, MB or GB from total (bytes)
  const humanReadableFileSize = (total: number) => {
    if (total < 1024) {
      return `${total} B`;
    }
    if (total < 1024 * 1024) {
      return `${(total / 1024).toFixed(2)} KB`;
    }
    if (total < 1024 * 1024 * 1024) {
      return `${(total / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(total / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const {
    progress = 0, total = 0, loaded = 0, rate = 0, step, error,
  } = resource?.uploadProgress || {};
  const hrUploadSpeed = humanReadableFileSize(rate);
  const timeRemaining = rate > 0 ? Math.round((total - loaded) / rate) : 0;

  // Get upload status message based on step
  const getUploadStatusMessage = () => {
    const isPdf = resource?.url?.toLowerCase().endsWith('.pdf') || resource?.title?.toLowerCase().endsWith('.pdf');

    if (!step || step === 'uploading') {
      if (progress < 0.99) {
        return `Uploading ${humanReadableFileSize(total)} at ${hrUploadSpeed}/s. ${humanReadableRemainingTime(
          timeRemaining,
        )} remaining...`;
      }
      return `Uploaded ${humanReadableFileSize(total)} successfully, ${isPdf ? 'analyzing PDF and extracting metadata...' : 'please wait while the file is processed...'}`;
    } if (step === 'preparing') {
      return 'Preparing upload, please wait...';
    } if (step === 'analyzing_link') {
      return 'Analyzing web page and extracting metadata...';
    } if (step === 'completing') {
      return `Upload complete (${humanReadableFileSize(total)}), ${isPdf ? 'analyzing PDF and extracting metadata...' : 'processing file...'}`;
    } if (step === 'error') {
      return `Error: ${error || 'Upload failed'}`;
    }
    return 'Processing...';
  };

  return (
    <Box
      flexDirection="row"
      bg="$bgCard"
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$md"
      overflow="hidden"
      sx={{
        _web: {
          transition: 'all 0.2s ease-in-out',
          ':hover': {
            borderColor: '$borderStrong',
            softShadow: '1',
          },
        },
      }}
    >
      {/* Icon area - accent colored */}
      <Box w={48} bg="$bgSubtle" alignItems="center" justifyContent="center" alignSelf="stretch" borderRightWidth={1} borderRightColor="$borderLight">
        <Icon as={resource?.type === 'link' ? LinkIcon : PaperclipIcon} size="md" color="$textSubtle" />
      </Box>

      {/* Content area */}
      <Box py="$2" px="$3" flex={1}>
        {inEditMode ? (
          <VStack space="xs" flex={1}>
            <Input size="sm">
              <InputField
                value={draft.title}
                placeholder="Resource Title"
                onChangeText={(text) => setDraft((prev) => ({ ...prev, title: text }))}
              />
            </Input>
            <Textarea size="sm">
              <TextareaInput
                value={draft.description}
                placeholder="Resource Description"
                onChangeText={(text) => setDraft((prev) => ({ ...prev, description: text }))}
              />
            </Textarea>
            <Input size="sm">
              <InputField
                value={draft.citation}
                placeholder="Citation Reference in APA Style"
                onChangeText={(text) => setDraft((prev) => ({ ...prev, citation: text }))}
              />
            </Input>
            {resource?.type === 'link' && (
            <Input size="sm">
              <InputField
                value={draft.url}
                placeholder="Link, e.g. www.google.com"
                onChangeText={(text) => setDraft((prev) => ({ ...prev, url: text }))}
              />
            </Input>
            )}
            <Button size="xs" onPress={saveChanges} testID={`resource-save-${resource.id}`}>
              <ButtonText>Save</ButtonText>
            </Button>
          </VStack>
        ) : (
          <VStack flex={1}>
            <HStack flex={1} mb="$2">
              <Heading flex={1} size="xs">
                {resource.title}
              </Heading>
              {!resource?.uploadProgress && (
              <HStack space="sm" justifyContent="flex-end">
                {!readonly && (
                <>
                  <Pressable onPress={onMoveUp} disabled={!canMoveUp} opacity={canMoveUp ? 1 : 0.3}>
                    <Icon size="xs" as={ChevronUp} />
                  </Pressable>
                  <Pressable onPress={onMoveDown} disabled={!canMoveDown} opacity={canMoveDown ? 1 : 0.3}>
                    <Icon size="xs" as={ChevronDown} />
                  </Pressable>
                  <Pressable onPress={toggleEditMode} testID={`resource-edit-${resource.id}`}>
                    <Icon size="xs" as={Pencil} />
                  </Pressable>
                  <Pressable onPress={onDelete}>
                    <Icon size="xs" as={Trash} />
                  </Pressable>
                </>
                )}
                <Pressable onPress={onOpen}>
                  <Icon size="xs" as={ExternalLink} />
                </Pressable>
              </HStack>
              )}
            </HStack>
            {resource?.uploadProgress ? (
              <>
                <Text size="xs">{getUploadStatusMessage()}</Text>
                <HStack flexGrow={1} alignItems="center">
                  <Progress flexGrow={1} value={Math.round((progress || 0) * 100)} w={500} size="xs">
                    <ProgressFilledTrack bg={step === 'error' ? '$statusError' : '$brandBase'} />
                  </Progress>
                  <Pressable ml="$2" onPress={onCancelUpload}>
                    <Icon size="sm" color="$textSubtle" as={CircleX} />
                  </Pressable>
                </HStack>
              </>
            ) : (
              <VStack space="sm">
                {(!!resource.description || !!resource.citation || (resource?.type === 'link' && !!resource.url)) && (
                  <VStack space="xs">
                    {!!resource.description && (
                      <Text size="xs" color="$textBody" lineHeight="$md">{resource.description as string}</Text>
                    )}
                    {!!resource.citation && (
                      <Text size="2xs" color="$textSubtle" lineHeight="$sm">{resource.citation}</Text>
                    )}
                    {resource?.type === 'link' && !!resource.url && (
                      <Text size="2xs" color="$textHint">{resource.url}</Text>
                    )}
                  </VStack>
                )}
                <ResourceDigestStatus
                  resource={resource}
                  onRetry={onRetryDigest}
                  isRetrying={isRetryingDigest}
                  digestPendingUntil={digestPendingUntil}
                  readonly={readonly}
                  showExamRelevant={showExamRelevant}
                  onOralExamRelevantChange={onOralExamRelevantChange}
                  isOralExamRelevantUpdating={isOralExamRelevantUpdating}
                />
              </VStack>
            )}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default ResourceCard;
